import { BusinessUnitSchema, ProvenanceExSchema, SeveritySchema, UserExSchema } from './iworklist.validation';
import { IVisitExaminationDTO } from '../models/visit';
import { z } from 'zod';

// Schema for IVisitExamDTO - extends IdDescriptionItem
export const IVisitExamDTOSchema = z
    .object({
        // From IdDescriptionItem
        id: z.string(),
        description: z.string().nullable(),

        // From IVisitExamDTO
        isMonitoring: z.boolean(),
        isRemoteVisit: z.boolean(),
    })
    .strict();

// Schema for IVisitPatient - should match the interface from vnext.models.ts ICommonWorklistPatientDTO and IVisitPatientSpecificDTO
export const IVisitPatientSchema = z
    .object({
        // From ICommonWorklistPatientDTO
        id: z.string(),
        sex: z.string().nullable(),
        sexCode: z.string().nullable(),
        taxCode: z.string().nullable(),
        birthDate: z.string().nullable(),
        race: z.string().nullable(),
        patientId: z.string().nullable(),
        issuer: z.string().nullable(),
        otherPatientId: z.string().nullable(),
        healthCard: z.string().nullable(),
        isAnonymous: z.boolean().nullable(),
        isMerged: z.boolean(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        birthPlace: z.string().nullable(),
        residenceProvince: z.string().nullable(),
        residenceCity: z.string().nullable(),
        residenceAddress: z.string().nullable(),
        residenceZipCode: z.string().nullable(),

        // From IVisitPatientSpecificDTO
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        isLocalPatient: z.boolean(),
        isCertificating: z.boolean().nullable(),
        idLocalPatient: z.string().nullable(),
        domicileAddress: z.string().nullable(),
        domicileCity: z.string().nullable(),
        domicileZipCode: z.string().nullable(),
        domicileProvince: z.string().nullable(),
    })
    .strict();

// Schema for Visit-specific command based on ICommandVisitExamination
export const ICommandVisitExaminationSchema = z
    .object({
        canBeDeferredAndDelete: z.boolean(),
        canBeSuspended: z.boolean(),
        canBeUndoSuspended: z.boolean(),
        canEditExam: z.boolean(),
        isMandatoryEquipmentChange: z.boolean(),
        canBeResigned: z.boolean(),
    })
    .strict();

export const VisitExaminationSchema = z
    .object({
        // From ICommonWorklistExaminationDTO (excluding patient)
        id: z.string(),
        idReport: z.string().nullable(),
        examinationDate: z.string().nullable(),
        reportDate: z.string().nullable(),
        approvalDate: z.string().nullable(),
        lastDate: z.string().nullable(),
        exam: IVisitExamDTOSchema,
        severity: SeveritySchema.nullable(),
        businessUnit: BusinessUnitSchema.nullable(),
        provenance: ProvenanceExSchema,
        referringPhysician: UserExSchema.nullable(),
        secondReferringPhysician: UserExSchema.nullable(),
        status: z.string().nullable(),
        isApproved: z.boolean(),
        isDigitallySigned: z.boolean(),
        imageAvailability: z.boolean(),
        publicationAuthorization: z.boolean(),
        revaluedCDA: z.boolean().nullable().optional(),

        idLockedUser: z.string().nullable(),
        lockedHost: z.string().nullable(),
        lockedUserFullName: z.string().nullable(),

        idCustomPage: z.string().nullable(),
        idCustomPageRoot: z.string().nullable(),
        idModelTypeRoots: z.array(z.string()),
        referringPhysicianFullNameList: z.string().nullable(),
        reportedExaminationsNumber: z.number().nullable().optional(),
        accessionNumber: z.string().nullable(),

        // From IVisitExaminationSpecificDTO
        monitoringStatus: z.string().nullable(),
        locked: z.boolean().optional(),
        isAdmitted: z.boolean(),
        isExecuted: z.boolean(),
        isReported: z.boolean(),
        isMerged: z.boolean(),

        examNumber: z.string().nullable(),
        isEcg: z.boolean(),
        isUs: z.boolean(),
        isEcgReport: z.boolean().optional(),
        reportingWithoutTrace: z.boolean(),

        addendumNumber: z.number().optional(),
        remoteVisitTask: z.number().nullable().optional(),
        localId: z.string().nullable(),
        fromIntegration: z.boolean(),
        sonographer: z.string().nullable(),
        nurse: UserExSchema.nullable(),
        nreMissing: z.boolean(),
        examinationsNumber: z.number().optional(),
        reportFromDDevice: z.boolean(),
        sameStudyInstanceNumber: z.number().nullable().optional(),
        idPackage: z.string().nullable().optional(),

        // Visit-specific command and additional properties
        command: ICommandVisitExaminationSchema,
        // Patient data - using IVisitPatient for DTO validation
        patient: IVisitPatientSchema,
    })
    .strict();

export const validateVisitExaminationDTO = (data: unknown): IVisitExaminationDTO => {
    return VisitExaminationSchema.parse(data) as IVisitExaminationDTO;
};

export const applyVisitExaminationValidation = (data: unknown, index: number) => {
    try {
        validateVisitExaminationDTO(data);
    } catch (error: any) {
        console.error(`Validation failed for examination at index ${index}:`, error.errors);
        throw new Error(`Invalid examination data at index ${index}: ${error.message}`);
    }
};
